import React, { useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import Icon from "../../../components/AppIcon";
import Button from "../../../components/ui/Button";

/**
 * Această versiune funcționează cu modelul backend:
 *  - name, description, category
 *  - duration_minutes (sau duration)
 *  - price_cents (sau price)
 *  - is_active (nu isActive)
 *  - assignedStaff / bookingsThisMonth pot lipsi => afișăm fallback-uri
 */

const ServiceTable = ({ services = [], onEdit, onDelete, onToggleStatus, highlightServiceId }) => {
  const { t } = useTranslation();
  const [sortField, setSortField] = useState("name"); // campul logic dupa care sortam
  const [sortDirection, setSortDirection] = useState("asc"); // asc | desc

  // mapare coduri categorie -> etichete traduse
  const CATEGORY_LABELS = {
    hair: t("services.categoryLabels.hair"),
    beauty: t("services.categoryLabels.beauty"),
    nails: t("services.categoryLabels.nails"),
    massage: t("services.categoryLabels.massage"),
    skincare: t("services.categoryLabels.skincare"),
    other: t("services.categoryLabels.other"),
  };

  // ===== Helpers de formatat din modelul backend =====
  const priceToNumber = (row) => {
    // întoarce un număr simplu (RON) pentru sortare/afișare
    if (row?.price_cents != null) return row.price_cents / 100;
    if (row?.price != null) return Number(row.price);
    return 0;
  };

  const formatPrice = (row) => {
    const value = priceToNumber(row);
    return value
      ? new Intl.NumberFormat("ro-RO", { style: "currency", currency: "RON" }).format(value)
      : "-";
  };

  const durationToMinutes = (row) => {
    // backend-ul tău are duration_minutes; dacă primești „duration", păstrăm fallback
    return row?.duration_minutes ?? row?.duration ?? 0;
  };

  const formatDuration = (row) => {
    const minutes = durationToMinutes(row);
    if (!minutes) return "-";
    const h = Math.floor(minutes / 60);
    const m = minutes % 60;
    return h > 0 ? `${h}h${m ? ` ${m}min` : ""}` : `${m}min`;
  };

  const getCategoryLabel = (row) => {
    // 1. dacă avem un label deja calculat, îl folosim
    if (row?.categoryLabel) return row.categoryLabel;

    // 2. dacă avem cod (ex: "massage", "hair"...), îl mapăm la eticheta frumoasă
    const raw = (row?.category || "").trim();
    if (!raw) return "-";

    const lower = raw.toLowerCase();
    if (CATEGORY_LABELS[lower]) {
      return CATEGORY_LABELS[lower];
    }

    // 3. fallback: afișăm ce e în DB
    return raw;
  };

  const getStaffArray = (row) => (Array.isArray(row?.assignedStaff) ? row.assignedStaff : []);
  const getBookings = (row) => Number(row?.bookingsThisMonth ?? 0);
  const isActive = (row) => !!row?.is_active; // ATTN: backend = is_active

  const isHighlighted = (row) =>
    highlightServiceId != null && String(row?.id) === String(highlightServiceId);

  // ===== Sortare: definim valorile „logice" pentru fiecare câmp =====
  const getSortValue = (row, field) => {
    switch (field) {
      case "name":
        return (row?.name || "").toLowerCase();
      case "category":
        return (getCategoryLabel(row) || "").toLowerCase();
      case "price":
        return priceToNumber(row);
      case "duration":
        return durationToMinutes(row);
      case "assignedStaff":
        return getStaffArray(row).length;
      case "bookings":
        return getBookings(row);
      case "status":
        return isActive(row) ? 1 : 0; // activ > inactiv
      default:
        return (row?.name || "").toLowerCase();
    }
  };

  const handleSort = (field) => {
    setSortDirection((d) => (sortField === field ? (d === "asc" ? "desc" : "asc") : "asc"));
    setSortField(field);
  };

  const sortedServices = useMemo(() => {
    const copy = [...services];
    return copy.sort((a, b) => {
      const av = getSortValue(a, sortField);
      const bv = getSortValue(b, sortField);

      if (av === bv) return 0;
      const cmp = av > bv ? 1 : -1;
      return sortDirection === "asc" ? cmp : -cmp;
    });
  }, [services, sortField, sortDirection]);

  const SortButton = ({ field, children }) => (
    <button
      onClick={() => handleSort(field)}
      className="flex items-center space-x-1 text-left font-medium text-foreground hover:text-primary transition-colors duration-150"
    >
      <span>{children}</span>
      <Icon
        name={
          sortField === field ? (sortDirection === "asc" ? "ChevronUp" : "ChevronDown") : "ChevronsUpDown"
        }
        size={16}
      />
    </button>
  );

  return (
    <div className="bg-background border border-border rounded-lg overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-muted border-b border-border">
            <tr>
              <th className="text-left p-4">
                {/* sortăm după nume */}
                <SortButton field="name">{t("services.columnService")}</SortButton>
              </th>
              <th className="text-left p-4">
                <SortButton field="category">{t("services.category")}</SortButton>
              </th>
              <th className="text-left p-4">
                <SortButton field="price">{t("common.price")}</SortButton>
              </th>
              <th className="text-left p-4">
                <SortButton field="duration">{t("common.duration")}</SortButton>
              </th>
              <th className="text-left p-4">
                <SortButton field="assignedStaff">{t("services.columnStaff")}</SortButton>
              </th>
              <th className="text-left p-4">
                <SortButton field="bookings">{t("services.columnBookings")}</SortButton>
              </th>
              <th className="text-left p-4">
                <SortButton field="status">{t("common.status")}</SortButton>
              </th>
              <th className="text-right p-4">{t("common.actions")}</th>
            </tr>
          </thead>

          <tbody>
            {sortedServices.map((service, index) => (
              <tr
                key={service?.id ?? index}
                data-service-row={service?.id}
                className={`border-b border-border hover:bg-muted/50 transition-colors duration-150 ${
                  isHighlighted(service) ? "bg-primary/10" : index % 2 === 0 ? "bg-background" : "bg-muted/20"
                }`}
              >
                {/* Nume + descriere */}
                <td className="p-4">
                  <div>
                    <h3 className="font-medium text-foreground">{service?.name || "-"}</h3>
                    {service?.description && (
                      <p className="text-sm text-text-secondary truncate max-w-xs">
                        {service.description}
                      </p>
                    )}
                  </div>
                </td>

                {/* Categorie */}
                <td className="p-4">
                  <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-accent/10 text-accent">
                    {getCategoryLabel(service)}
                  </span>
                </td>

                {/* Preț */}
                <td className="p-4">
                  <span className="font-semibold text-foreground">{formatPrice(service)}</span>
                </td>

                {/* Durată */}
                <td className="p-4">
                  <span className="text-foreground">{formatDuration(service)}</span>
                </td>

                {/* Personal (poate lipsi) */}
                <td className="p-4">
                  <div className="flex -space-x-2">
                    {getStaffArray(service)
                      .slice(0, 3)
                      .map((staff) => (
                        <div
                          key={staff?.id}
                          className="w-8 h-8 bg-primary rounded-full flex items-center justify-center border-2 border-background"
                          title={staff?.name}
                        >
                          <Icon name="User" size={14} color="white" />
                        </div>
                      ))}
                    {getStaffArray(service).length > 3 && (
                      <div className="w-8 h-8 bg-muted rounded-full flex items-center justify-center border-2 border-background text-xs font-medium text-text-secondary">
                        +{getStaffArray(service).length - 3}
                      </div>
                    )}
                    {getStaffArray(service).length === 0 && (
                      <span className="text-sm text-text-secondary">—</span>
                    )}
                  </div>
                </td>

                {/* Programări în luna curentă (fallback 0) */}
                <td className="p-4">
                  <div className="flex items-center space-x-2">
                    <Icon name="Calendar" size={16} className="text-text-secondary" />
                    <span className="text-foreground">{getBookings(service)}</span>
                  </div>
                </td>

                {/* Status (backend = is_active) */}
                <td className="p-4">
                  <div
                    className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                      isActive(service) ? "bg-success/10 text-success" : "bg-muted text-text-secondary"
                    }`}
                  >
                    <div
                      className={`w-2 h-2 rounded-full mr-2 ${
                        isActive(service) ? "bg-success" : "bg-text-secondary"
                      }`}
                    />
                    {isActive(service) ? t("common.active") : t("common.inactive")}
                  </div>
                </td>

                {/* Acțiuni */}
                <td className="p-4">
                  <div className="flex items-center justify-end space-x-1">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => onToggleStatus?.(service?.id)}
                      className="w-8 h-8"
                      title={isActive(service) ? t("services.deactivate") : t("services.activate")}
                    >
                      <Icon name={isActive(service) ? "EyeOff" : "Eye"} size={16} />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => onEdit?.(service)}
                      className="w-8 h-8"
                      title={t("common.edit")}
                    >
                      <Icon name="Edit" size={16} />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => onDelete?.(service?.id)}
                      className="w-8 h-8 text-error hover:text-error"
                      title={t("common.delete")}
                    >
                      <Icon name="Trash2" size={16} />
                    </Button>
                  </div>
                </td>
              </tr>
            ))}

            {sortedServices.length === 0 && (
              <tr>
                <td colSpan={8} className="p-6 text-center text-text-secondary">
                  {t("services.noServicesTable")}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ServiceTable;
